import { useState, useEffect, useCallback, useRef } from 'react';
import { Mail, Phone, Linkedin, MessageSquare, RefreshCw, Copy, Check, Users, CircleAlert as AlertCircle, Loader as Loader2, CircleHelp as HelpCircle, Target, ArrowRight } from 'lucide-react';
import { logAccountActivity } from '../../../lib/activityLog';
import { canInvokeEdgeFn, releaseEdgeFn } from '../../../lib/edgeFnGuard';
import { Sentry } from '../../../lib/sentry';
import { useApi } from '../../../lib/useApi';
import { useLlmJobsPoll } from '../../../lib/useLlmJobsPoll';
import { useRealtimeStatus } from '../../../lib/useRealtimeStatus';
import type { Account, Contact, ContactOutreach, UseCaseItem, ContactUseCaseScore, CallBattlecard } from '../../../types';
import {
  getHighlightFromPriority,
  HIGHLIGHT_COLORS,
  PRIORITY_LABELS,
  PRIORITY_ORDER,
} from '../../../lib/orgChartUtils';
import type { OutboundPriority } from '../../../types';
import EditableOutreachCard from './EditableOutreachCard';
import CallBattlecardCard from './CallBattlecardCard';
import PersonaActivitySection from './PersonaActivitySection';
import type { SelectedTriggerContext } from '../triggers/TriggerContextSelector';

export type ToneKey = 'executive' | 'warm_professional' | 'provocative' | 'consultative' | 'casual';

const TONE_OPTIONS: { key: ToneKey; label: string; description: string }[] = [
  { key: 'executive', label: 'Executive', description: 'Formal, insight-led, no pleasantries' },
  { key: 'warm_professional', label: 'Warm Professional', description: 'Personable yet business-focused' },
  { key: 'provocative', label: 'Provocative', description: 'Bold, challenger-style, pattern-breaking' },
  { key: 'consultative', label: 'Consultative', description: 'Peer-level, technical depth' },
  { key: 'casual', label: 'Casual', description: 'Conversational, minimal formality' },
];

// True once a row actually holds generated material. A row can exist with every field empty:
// the launch claims it up front so there is something to poll before the first run finishes.
function hasOutreachContent(row: ContactOutreach): boolean {
  return Boolean(
    row.cold_call_script
    || row.linkedin_message
    || row.email_draft
    || row.contact_message
    || row.why_should_they_care
    || row.call_battlecard
    || (row.discovery_questions && row.discovery_questions.length > 0),
  );
}

interface Props {
  account: Account;
  contacts: Contact[];
  selectedUseCase: UseCaseItem | null;
  ucScores: ContactUseCaseScore[];
  vendorName: string;
  preSelectedContactId: string | null;
  triggerContext?: SelectedTriggerContext | null;
}

export default function ContactOutreachSubTab({
  account,
  contacts,
  selectedUseCase,
  ucScores,
  vendorName,
  preSelectedContactId,
  triggerContext,
}: Props) {
  const { apiGet, apiPost, apiPut } = useApi();
  const [selectedContactId, setSelectedContactId] = useState<string | null>(preSelectedContactId);
  const [outreach, setOutreach] = useState<ContactOutreach | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const lastUseCaseRef = useRef<string | null>(null);
  const [selectedTone, setSelectedTone] = useState<ToneKey>('executive');

  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [adjustingField, setAdjustingField] = useState<string | null>(null);
  const [adjustVersion, setAdjustVersion] = useState(0);

  // Mirrors `generating` for the poll callbacks, which must act on a terminal row exactly
  // once even if two poll ticks land before React re-renders.
  const generatingRef = useRef(false);
  const setGeneratingState = useCallback((value: boolean) => {
    generatingRef.current = value;
    setGenerating(value);
  }, []);

  const scoreMap = new Map<string, ContactUseCaseScore>();
  for (const s of ucScores) {
    scoreMap.set(s.contact_id, s);
  }

  const rankedContacts = [...contacts].sort((a, b) => {
    const scoreA = scoreMap.get(a.id);
    const scoreB = scoreMap.get(b.id);
    const priA = scoreA ? (PRIORITY_ORDER[scoreA.outbound_priority as OutboundPriority] ?? 4) : 4;
    const priB = scoreB ? (PRIORITY_ORDER[scoreB.outbound_priority as OutboundPriority] ?? 4) : 4;
    if (priA !== priB) return priA - priB;
    const relA = scoreA?.use_case_relevance_score ?? 0;
    const relB = scoreB?.use_case_relevance_score ?? 0;
    return relB - relA;
  });

  useEffect(() => {
    if (preSelectedContactId) {
      setSelectedContactId(preSelectedContactId);
    }
  }, [preSelectedContactId]);

  useEffect(() => {
    const ucTitle = selectedUseCase?.title ?? null;
    if (lastUseCaseRef.current !== null && lastUseCaseRef.current !== ucTitle) {
      // Clear outreach data when use case changes, but keep selected contact
      setOutreach(null);
    }
    lastUseCaseRef.current = ucTitle;
  }, [selectedUseCase?.title]);

  // The row for the current (contact, use case) pair. Shared by the initial load and by the
  // generation poll below, which watch the same row.
  const fetchOutreachRow = useCallback(async (contactId: string): Promise<ContactOutreach | null> => {
    if (!selectedUseCase) return null;
    const data = await apiGet<{ outreach: ContactOutreach[] }>(
      `/v1/contacts/${contactId}/outreach`,
    );
    return (data.outreach || []).find(o => o.use_case_title === selectedUseCase.title) ?? null;
  }, [selectedUseCase, apiGet]);

  const loadOutreach = useCallback(async (contactId: string) => {
    if (!selectedUseCase) return;
    try {
      const match = await fetchOutreachRow(contactId);
      // A row claimed by a generation that has not produced anything yet is a placeholder,
      // not content: render the empty state rather than a set of blank cards.
      setOutreach(match && !hasOutreachContent(match) ? null : match);
      // Pick the generation back up after a reload or a contact switch — the run carries on
      // server-side regardless of whether anyone is watching.
      setGeneratingState(match?.status === 'processing');
    } catch {
      setOutreach(null);
    }
  }, [selectedUseCase, fetchOutreachRow, setGeneratingState]);

  useEffect(() => {
    if (selectedContactId) loadOutreach(selectedContactId);
    else setOutreach(null);
  }, [selectedContactId, loadOutreach]);

  // Generation runs as a background llm_job: nudge the owning lambda to drain its in-flight
  // runs (retrieve -> finalize) while we are watching one.
  useLlmJobsPoll(account.org_id, generating, '/v1/outreach/llm-jobs/check');

  // ...and watch the domain row the finalize writes into, exactly as PersonaActivitySection
  // watches contacts.activity_research_status.
  useRealtimeStatus({
    table: 'contact_outreach',
    filterColumn: 'contact_id',
    filterValue: selectedContactId ?? undefined,
    enabled: generating,
    statusField: 'status',
    terminalStatuses: ['completed', 'error'],
    fallbackIntervalMs: 3000,
    fetcher: useCallback(async () => {
      if (!selectedContactId) return null;
      const row = await fetchOutreachRow(selectedContactId);
      return row as unknown as Record<string, unknown> | null;
    }, [fetchOutreachRow, selectedContactId]),
    onUpdate: useCallback((raw: Record<string, unknown>) => {
      const row = raw as unknown as ContactOutreach;
      if (row.status === 'processing' || !generatingRef.current) return;

      setGeneratingState(false);
      // Released here rather than when the POST returns: the POST now returns in
      // milliseconds, long before the generation it started has finished.
      releaseEdgeFn(`outreach-${row.contact_id}`);

      if (row.status === 'error') {
        setError(row.error_message || 'Outreach generation failed. Please try again.');
        return;
      }

      setOutreach(row);
      setDirtyFields(new Set());
      const contact = contacts.find(c => c.id === row.contact_id);
      logAccountActivity(
        account.id,
        'outreach_generated',
        `Generated outreach for ${contact ? `${contact.first_name} ${contact.last_name}` : 'contact'}`,
      );
    }, [contacts, account.id, setGeneratingState]),
  });

  const hasDirtyFields = dirtyFields.size > 0;

  const handleContactSwitch = (contactId: string) => {
    if (hasDirtyFields && contactId !== selectedContactId) {
      const confirmed = window.confirm('You have unsaved changes. Discard and switch contacts?');
      if (!confirmed) return;
    }
    setDirtyFields(new Set());
    setSelectedContactId(contactId);
  };

  const doGenerate = async () => {
    if (!selectedContactId || !selectedUseCase) return;
    if (!canInvokeEdgeFn(`outreach-${selectedContactId}`)) return;
    const contact = contacts.find(c => c.id === selectedContactId);
    if (!contact) return;

    setGeneratingState(true);
    setError('');

    try {
      // 202 in milliseconds: the two-call pipeline runs off the request path, so this no
      // longer sits behind API Gateway's 29s integration cap. The polls above pick the
      // result up when the row turns terminal.
      await apiPost('/v1/generate-contact-outreach', {
        account_id: account.id,
        contact_id: contact.id,
        use_case_title: selectedUseCase.title,
        tone: selectedTone,
        ...(triggerContext ? {
          trigger_title: triggerContext.title,
          trigger_why_now: triggerContext.why_now,
          trigger_evidence: triggerContext.evidence,
          trigger_angle: triggerContext.angle,
        } : {}),
      });
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'outreach_generation' } });
      setError(err instanceof Error ? err.message : 'Generation failed');
      setGeneratingState(false);
      releaseEdgeFn(`outreach-${contact.id}`);
    }
  };

  const generateOutreach = async () => {
    if (outreach && hasDirtyFields) {
      setShowOverwriteConfirm(true);
      return;
    }
    if (outreach) {
      const confirmed = window.confirm(
        'This will regenerate all outreach messages, overwriting the current versions. Continue?'
      );
      if (!confirmed) return;
    }
    doGenerate();
  };

  const confirmOverwrite = () => {
    setShowOverwriteConfirm(false);
    doGenerate();
  };

  const handleFieldSave = async (fieldKey: string, value: string) => {
    if (!outreach) return;
    // Backend whitelists the editable draft fields and resolves the owning account
    // from the outreach row (legacy UPDATE RLS is_account_org_member(account_id)).
    try {
      await apiPut(`/v1/outreach/${outreach.id}`, { [fieldKey]: value });
      setOutreach(prev => prev ? { ...prev, [fieldKey]: value } : prev);
      setDirtyFields(prev => {
        const next = new Set(prev);
        next.delete(fieldKey);
        return next;
      });
    } catch {
      // silent — keep prior no-op-on-error behaviour
    }
  };

  const handleDirtyChange = useCallback((fieldKey: string, dirty: boolean) => {
    setDirtyFields(prev => {
      const next = new Set(prev);
      if (dirty) next.add(fieldKey);
      else next.delete(fieldKey);
      return next;
    });
  }, []);

  const handleBattlecardSave = async (updated: CallBattlecard) => {
    if (!outreach) return;
    try {
      await apiPut(`/v1/outreach/${outreach.id}`, { call_battlecard: updated });
      setOutreach(prev => prev ? { ...prev, call_battlecard: updated } : prev);
      setDirtyFields(prev => {
        const next = new Set(prev);
        next.delete('call_battlecard');
        return next;
      });
    } catch {
      // silent
    }
  };

  const adjustToWordRange = async (fieldKey: string, minWords: number, maxWords: number) => {
    if (!outreach) return;
    const currentText = (outreach as unknown as Record<string, unknown>)[fieldKey] as string;
    if (!currentText) return;

    setAdjustingField(fieldKey);

    try {
      const result = await apiPost<{ adjusted_text?: string }>('/v1/adjust-outreach-length', {
        outreach_id: outreach.id,
        field: fieldKey,
        current_text: currentText,
        min_words: minWords,
        max_words: maxWords,
        tone: selectedTone,
      });
      if (result.adjusted_text) {
        setOutreach(prev => prev ? { ...prev, [fieldKey]: result.adjusted_text } : prev);
        setAdjustVersion(v => v + 1);
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'outreach_adjust_word_range' } });
      setError(err instanceof Error ? err.message : 'Adjustment failed');
    } finally {
      setAdjustingField(null);
    }
  };

  const copyText = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  useEffect(() => {
    if (!hasDirtyFields) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasDirtyFields]);

  const selectedContact = contacts.find(c => c.id === selectedContactId);
  const selectedScore = selectedContactId ? scoreMap.get(selectedContactId) : undefined;

  if (contacts.length === 0) {
    return (
      <div className="bg-app-canvas border border-app-line rounded-lg p-10 text-center">
        <Users className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
        <h3 className="text-base font-semibold text-app-ink mb-1">No Contacts Available</h3>
        <p className="text-sm text-app-muted">Upload contacts in the Org Chart tab first.</p>
      </div>
    );
  }

  if (!selectedUseCase) {
    return (
      <div className="bg-app-warning/10 border border-app-warning rounded-lg p-8 text-center">
        <HelpCircle className="w-10 h-10 text-app-warning mx-auto mb-3" />
        <h3 className="text-base font-semibold text-app-warning mb-1">Select a Use Case</h3>
        <p className="text-sm text-app-warning">Choose a use case from the dropdown above to generate outreach assets.</p>
      </div>
    );
  }

  let currentGroup: OutboundPriority | null = null;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(16rem,300px)_minmax(0,1fr)]">
      <div className="space-y-2">
        <p className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-1">
          Ranked Contacts
        </p>
        <p className="text-[10px] text-app-muted mb-3">
          For: {selectedUseCase.title}
        </p>
        <div className="max-h-72 space-y-1 overflow-y-auto lg:max-h-[600px]">
          {rankedContacts.map(contact => {
            const score = scoreMap.get(contact.id);
            const priority = (score?.outbound_priority || 'low_relevance') as OutboundPriority;
            const highlight = getHighlightFromPriority(priority);
            const colors = HIGHLIGHT_COLORS[highlight];
            const isSelected = selectedContactId === contact.id;

            let showGroupHeader = false;
            if (priority !== currentGroup) {
              currentGroup = priority;
              showGroupHeader = true;
            }

            return (
              <div key={contact.id}>
                {showGroupHeader && (
                  <div className="flex items-center gap-2 pt-3 pb-1.5 px-1">
                    <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${colors.text}`}>
                      {PRIORITY_LABELS[priority] || 'Other'}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => handleContactSwitch(contact.id)}
                  className={`w-full text-left p-3 rounded-lg border transition ${
                    isSelected
                      ? 'border-forest-500 bg-forest-50 ring-1 ring-forest-200'
                      : 'border-app-line hover:border-app-line-strong bg-app-surface'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors.dot}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-app-ink truncate">
                        {contact.first_name} {contact.last_name}
                      </p>
                      <p className="text-[11px] text-app-muted truncate">{contact.role}</p>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0">
                      <span className="text-[10px] font-medium text-app-muted">
                        {score?.use_case_relevance_score ?? 0}%
                      </span>
                      {score?.persona_match && (
                        <span className="text-[9px] text-forest-600 truncate max-w-[80px]">
                          {score.persona_match}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-4">
        {!selectedContact ? (
          <div className="bg-app-canvas border border-app-line rounded-lg p-10 text-center">
            <MessageSquare className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
            <p className="text-sm text-app-muted">Select a contact to view or generate outreach.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-app-ink">
                  {selectedContact.first_name} {selectedContact.last_name}
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-xs text-app-muted">{selectedContact.role}</p>
                  {selectedScore && (
                    <>
                      <span className="text-xs text-app-line-strong">|</span>
                      <span className={`text-xs font-medium ${
                        HIGHLIGHT_COLORS[getHighlightFromPriority(selectedScore.outbound_priority as OutboundPriority)].text
                      }`}>
                        {PRIORITY_LABELS[selectedScore.outbound_priority as OutboundPriority]}
                      </span>
                    </>
                  )}
                </div>
                <p className="text-[11px] text-app-muted mt-0.5">{selectedUseCase.title}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Tone</span>
                  <select
                    value={selectedTone}
                    onChange={e => setSelectedTone(e.target.value as ToneKey)}
                    className="text-xs border border-app-line-strong rounded-lg px-2 py-1.5 bg-app-surface text-app-ink focus:outline-none focus:ring-1 focus:ring-forest-400 focus:border-forest-400"
                  >
                    {TONE_OPTIONS.map(t => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={generateOutreach}
                  disabled={generating}
                  className="btn-primary !py-2 !px-4 !text-sm"
                >
                  {generating ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-1.5" /> {outreach ? 'Regenerate' : 'Generate Outreach'}</>
                  )}
                </button>
              </div>
            </div>

            {selectedTone !== 'executive' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-app-canvas border border-app-line rounded-lg">
                <span className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Tone:</span>
                <span className="text-xs text-app-muted font-medium">{TONE_OPTIONS.find(t => t.key === selectedTone)?.label}</span>
                <span className="text-[10px] text-app-muted">{TONE_OPTIONS.find(t => t.key === selectedTone)?.description}</span>
                <span className="text-[10px] text-app-muted ml-auto">Applies to LinkedIn and Email only</span>
              </div>
            )}

            {showOverwriteConfirm && (
              <div className="p-4 bg-app-warning/10 border border-app-warning rounded-lg">
                <p className="text-sm text-app-warning font-medium mb-2">
                  You have unsaved edits. Regenerating will overwrite all current messages.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={confirmOverwrite}
                    className="px-3 py-1.5 text-xs font-medium bg-app-warning text-white rounded-lg hover:bg-app-warning transition-colors"
                  >
                    Overwrite & Regenerate
                  </button>
                  <button
                    onClick={() => setShowOverwriteConfirm(false)}
                    className="px-3 py-1.5 text-xs font-medium text-app-muted hover:text-app-ink transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {selectedScore?.priority_rationale && (
              <div className="p-3 bg-app-focus/10 border border-app-focus rounded-lg">
                <p className="text-[10px] font-semibold text-app-focus uppercase tracking-wider mb-1">Outreach Strategy</p>
                <p className="text-xs text-app-focus leading-relaxed">{selectedScore.priority_rationale}</p>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg">
                <AlertCircle className="w-4 h-4 text-app-critical flex-shrink-0" />
                <p className="text-xs text-app-critical">{error}</p>
              </div>
            )}

            {generating && (
              <div className="flex items-center gap-3 p-4 bg-forest-50 border border-forest-100 rounded-lg">
                <Loader2 className="w-5 h-5 text-forest-600 animate-spin flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-forest-800">
                    Generating outreach for {selectedContact.first_name} {selectedContact.last_name}
                  </p>
                  <p className="text-xs text-forest-600">
                    Writing the call script, LinkedIn message, email draft and discovery questions.
                    This typically takes one to two minutes — you can leave this tab and come back.
                  </p>
                </div>
              </div>
            )}

            {outreach ? (
              <div className="space-y-4">
                {outreach.why_should_they_care && (
                  <div className="rounded-lg border border-app-warning bg-app-warning/10 shadow-none overflow-hidden">
                    <div className="bg-app-warning px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-white" />
                        <h4 className="text-sm font-semibold text-white">Why Should They Care</h4>
                      </div>
                      <button
                        onClick={() => copyText(outreach.why_should_they_care, 'care')}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
                      >
                        {copied === 'care' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copied === 'care' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="p-5">
                      <ul className="space-y-2">
                        {outreach.why_should_they_care
                          .split('\n')
                          .map(line => line.replace(/^[\s\-\*\u2022\u25CF\u25CB]+/, '').trim())
                          .filter(line => line.length > 0)
                          .map((bullet, i) => (
                            <li key={i} className="flex items-start gap-2.5">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-app-warning flex-shrink-0" />
                              <span className="text-sm font-medium text-app-warning leading-relaxed">{bullet}</span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                )}

                {outreach.red_thread_summary && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-app-canvas border border-app-line rounded-lg">
                    <ArrowRight className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />
                    <p className="text-[11px] text-app-muted leading-relaxed">
                      <span className="font-semibold text-app-muted">Red Thread:</span> {outreach.red_thread_summary}
                    </p>
                  </div>
                )}

                <PersonaActivitySection account={account} contact={selectedContact} />
                {outreach.call_battlecard ? (
                  <CallBattlecardCard
                    battlecard={outreach.call_battlecard}
                    onSave={handleBattlecardSave}
                    hasUnsavedChanges={handleDirtyChange}
                  />
                ) : outreach.cold_call_script ? (
                  <EditableOutreachCard
                    key={`cold_call_script-${adjustVersion}`}
                    title="Cold Call Script"
                    icon={<Phone className="w-4 h-4 text-white" />}
                    fieldKey="cold_call_script"
                    content={outreach.cold_call_script}
                    headerColor="bg-app-positive"
                    cardColor="bg-app-positive/10 border-app-positive"
                    onSave={handleFieldSave}
                    hasUnsavedChanges={handleDirtyChange}
                  />
                ) : null}
                <EditableOutreachCard
                  key={`linkedin_message-${adjustVersion}`}
                  title="LinkedIn Message"
                  icon={<Linkedin className="w-4 h-4 text-white" />}
                  fieldKey="linkedin_message"
                  content={outreach.linkedin_message}
                  headerColor="bg-app-focus"
                  cardColor="bg-app-focus/10 border-app-focus/30"
                  onSave={handleFieldSave}
                  hasUnsavedChanges={handleDirtyChange}
                  showWordControls
                  onAdjustToWordRange={adjustToWordRange}
                  adjustingWords={adjustingField === 'linkedin_message'}
                  charLimit={300}
                />
                <EditableOutreachCard
                  key={`email_draft-${adjustVersion}`}
                  title="Email Draft"
                  icon={<Mail className="w-4 h-4 text-white" />}
                  fieldKey="email_draft"
                  content={outreach.email_draft}
                  headerColor="bg-app-focus"
                  cardColor="bg-app-focus/10 border-app-focus"
                  onSave={handleFieldSave}
                  hasUnsavedChanges={handleDirtyChange}
                  showWordControls
                  onAdjustToWordRange={adjustToWordRange}
                  adjustingWords={adjustingField === 'email_draft'}
                />
                {outreach.discovery_questions && outreach.discovery_questions.length > 0 && (
                  <div className="rounded-lg border border-app-line bg-app-surface shadow-none overflow-hidden">
                    <div className="bg-app-ink px-5 py-3 flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-white" />
                      <h4 className="text-sm font-semibold text-white">Discovery Questions</h4>
                    </div>
                    <div className="p-5 space-y-3">
                      {outreach.discovery_questions.map((q, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-app-canvas text-app-muted flex items-center justify-center text-xs font-bold">
                            {i + 1}
                          </span>
                          <p className="text-sm text-app-ink leading-relaxed">{q}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <PersonaActivitySection account={account} contact={selectedContact} />
                {!generating && (
                  <div className="bg-app-canvas border border-app-line rounded-lg p-10 text-center">
                    <MessageSquare className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
                    <p className="text-sm text-app-muted">
                      Click "Generate Outreach" to create personalised messaging for this contact.
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
